/**
 * Call Utility Functions
 *
 * Shared utilities for parsing call data from the getActiveCalls API
 * and Task Router responses.
 */

/**
 * Check if a value looks like a valid patient name (not a timestamp or other invalid data)
 *
 * @param {string} value - Value to check
 * @returns {boolean} - True if value looks like a valid name
 */
const isValidPatientName = (value) => {
  if (!value || typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed) return false;

  // Reject if it looks like a timestamp (ISO format or similar)
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return false;
  if (/^\d{2}\/\d{2}\/\d{4}/.test(trimmed)) return false;
  if (/T\d{2}:\d{2}:\d{2}/.test(trimmed)) return false;

  // Reject if it's just numbers
  if (/^[\d\s\-./]+$/.test(trimmed)) return false;

  return true;
};

/**
 * Parse patients array from a call object
 *
 * Handles multiple data sources:
 * 1. call.patients - Direct patients array (JSON string or array)
 * 2. taskAttributes.outboundPatients - For outbound calls
 * 3. call.patientFirstName/patientLastName - Direct fields on call object
 * 4. taskAttributes.name - Fallback for video calls
 *
 * @param {Object} call - Call object from getActiveCalls or Task Router
 * @returns {Array} - Array of patient objects with patientFirstName, patientLastName, etc.
 *
 * @example
 * // Returns [{ patientFirstName: 'John', patientLastName: 'Doe', dob: '1990-01-01' }]
 * parseCallPatients({ patients: '[{"patientFirstName":"John","patientLastName":"Doe"}]' })
 */
export const parseCallPatients = (call) => {
  if (!call) return [];

  // Try to get patients from call.patients field first
  if (call.patients) {
    let patients = call.patients;
    if (typeof patients === 'string') {
      try {
        patients = JSON.parse(patients);
      } catch {
        patients = [];
      }
    }
    if (Array.isArray(patients) && patients.length > 0) {
      // Filter out invalid patient entries
      const validPatients = patients.filter(
        (p) =>
          isValidPatientName(p.patientFirstName) ||
          isValidPatientName(p.patientLastName)
      );
      if (validPatients.length > 0) {
        return validPatients;
      }
    }
  }

  // Try to get from taskAttributes (may be object or JSON string)
  const attrs = parseTaskAttributes(call.taskAttributes);

  // Try outboundPatients (for outbound calls)
  if (attrs.outboundPatients) {
    let outboundPatients = attrs.outboundPatients;
    if (typeof outboundPatients === 'string') {
      try {
        outboundPatients = JSON.parse(outboundPatients);
      } catch {
        outboundPatients = [];
      }
    }
    if (Array.isArray(outboundPatients) && outboundPatients.length > 0) {
      const validPatients = outboundPatients.filter(
        (p) =>
          isValidPatientName(p.patientFirstName) ||
          isValidPatientName(p.patientLastName)
      );
      if (validPatients.length > 0) {
        return validPatients;
      }
    }
  }

  // Fallback: try direct fields on call object (patientFirstName, patientLastName)
  if (
    isValidPatientName(call.patientFirstName) ||
    isValidPatientName(call.patientLastName)
  ) {
    return [
      {
        patientFirstName: isValidPatientName(call.patientFirstName)
          ? call.patientFirstName
          : null,
        patientLastName: isValidPatientName(call.patientLastName)
          ? call.patientLastName
          : null,
        dob: call.dob || null,
        caseID: call.caseID || null,
      },
    ];
  }

  // Fallback: use name from taskAttributes (video calls often have this)
  if (isValidPatientName(attrs.name)) {
    // Try to split name into first/last
    const nameParts = attrs.name.trim().split(/\s+/);
    if (nameParts.length >= 2) {
      return [
        {
          patientFirstName: nameParts[0],
          patientLastName: nameParts.slice(1).join(' '),
        },
      ];
    }
    return [{ patientFirstName: attrs.name, patientLastName: '' }];
  }

  // Fallback: try videoCallerName from taskAttributes
  if (isValidPatientName(attrs.videoCallerName)) {
    const nameParts = attrs.videoCallerName.trim().split(/\s+/);
    if (nameParts.length >= 2) {
      return [
        {
          patientFirstName: nameParts[0],
          patientLastName: nameParts.slice(1).join(' '),
        },
      ];
    }
    return [{ patientFirstName: attrs.videoCallerName, patientLastName: '' }];
  }

  return [];
};

/**
 * Parse task attributes from a call object
 *
 * Handles both JSON string and object formats.
 *
 * @param {string|Object|null|undefined} taskAttributes - Task attributes
 * @returns {Object} - Parsed attributes object
 */
export const parseTaskAttributes = (taskAttributes) => {
  if (!taskAttributes) return {};

  if (typeof taskAttributes === 'string') {
    try {
      return JSON.parse(taskAttributes);
    } catch {
      return {};
    }
  }

  return taskAttributes;
};

/**
 * Extract site code from call's task attributes
 *
 * Checks multiple possible field names for site code:
 * 1. videoSiteCode - video calls
 * 2. siteCode/sitecode - general
 * 3. dnisName - voice calls (format: "SiteCode +phone")
 * 4. outboundPatients[].sitecode - outbound calls
 *
 * @param {Object} call - Call object with taskAttributes
 * @returns {string|null} - Site code or null if not found
 */
export const extractSiteFromCall = (call) => {
  const attrs = parseTaskAttributes(call?.taskAttributes);

  // Direct siteCode fields (video calls)
  if (attrs.videoSiteCode) return attrs.videoSiteCode;
  if (attrs.siteCode) return attrs.siteCode;
  if (attrs.sitecode) return attrs.sitecode;

  // Try dnisName for voice calls (format: "SJOG-Berwick +61459623174")
  if (attrs.dnisName) {
    const match = attrs.dnisName.match(/^([A-Za-z0-9-]+)\s*\+/);
    if (match) return match[1];
  }

  // Try outboundPatients for outbound calls
  if (attrs.outboundPatients) {
    let outboundPatients = attrs.outboundPatients;
    if (typeof outboundPatients === 'string') {
      try {
        outboundPatients = JSON.parse(outboundPatients);
      } catch {
        outboundPatients = [];
      }
    }
    if (Array.isArray(outboundPatients) && outboundPatients.length > 0) {
      const firstPatient = outboundPatients[0];
      if (firstPatient.sitecode) return firstPatient.sitecode;
    }
  }

  return null;
};

/**
 * Parse triage category from call's task attributes
 *
 * @param {Object} call - Call object with taskAttributes
 * @returns {string|null} - Triage category or null if not found
 */
export const parseTriageCatFromCall = (call) => {
  const attrs = parseTaskAttributes(call?.taskAttributes);
  return attrs.triageCat || null;
};

/**
 * Translate call status to human-readable form
 *
 * @param {string} status - Call status from Task Router
 * @returns {string} - Human-readable status
 */
export const translateCallStatus = (status) => {
  switch (status) {
    case 'pending':
      return 'In Queue';
    case 'reserved':
      return 'Reserved';
    case 'assigned':
      return 'With Agent';
    case 'wrapping':
      return 'Wrapping';
    case 'completed':
      return 'Completed';
    case 'canceled':
      return 'Canceled';
    default:
      return status || 'Unknown';
  }
};

/**
 * Valid active call statuses from Twilio TaskRouter
 */
export const ACTIVE_CALL_STATUSES = [
  'pending',
  'reserved',
  'assigned',
  'wrapping',
];

/**
 * Check if a call status is an active status
 *
 * @param {string} status - Call status
 * @returns {boolean} - True if status is active
 */
export const isActiveCallStatus = (status) => {
  return ACTIVE_CALL_STATUSES.includes(status);
};
